import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  X, 
  Download, 
  Printer, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Maximize2, 
  Minimize2, 
  FileText, 
  Table, 
  Presentation, 
  Award, 
  Search, 
  Copy, 
  Check, 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Pause, 
  Share2, 
  CloudUpload, 
  CheckCircle2, 
  AlertCircle, 
  Info, 
  Layers, 
  ExternalLink,
  Filter,
  ArrowUpDown,
  FileSpreadsheet,
  FileCheck,
  ShieldCheck,
  Calendar,
  Building2,
  Landmark
} from 'lucide-react';
import { ViewerDocument, SpreadsheetSheet, PresentationSlide } from '../types/documentViewer';

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: ViewerDocument | null;
  onSaveToFirestore?: (doc: ViewerDocument) => Promise<void>;
  isSavingToFirestore?: boolean;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
  isOpen,
  onClose,
  document,
  onSaveToFirestore,
  isSavingToFirestore = false
}) => {
  // State variables
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [rotation, setRotation] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeSheetIndex, setActiveSheetIndex] = useState<number>(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const [isSlideshowRunning, setIsSlideshowRunning] = useState<boolean>(false);
  const [showSlideNotes, setShowSlideNotes] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showMetadataDrawer, setShowMetadataDrawer] = useState<boolean>(false);
  const [copiedText, setCopiedText] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number; val: any } | null>(null);
  const [sortColumn, setSortColumn] = useState<{ index: number; direction: 'asc' | 'desc' } | null>(null);
  const [firestoreSavedSuccess, setFirestoreSavedSuccess] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const slideshowTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset states when document changes
  useEffect(() => {
    if (document) {
      setZoomLevel(100);
      setRotation(0);
      setActiveSheetIndex(0);
      setActiveSlideIndex(0);
      setIsSlideshowRunning(false);
      setSearchQuery('');
      setSelectedCell(null);
      setSortColumn(null);
      setFirestoreSavedSuccess(document.metadata?.isSavedToFirestore || false);
    }
  }, [document]);

  // Slideshow auto-advance
  useEffect(() => {
    if (isSlideshowRunning && document?.presentationSlides?.length) {
      slideshowTimerRef.current = setInterval(() => {
        setActiveSlideIndex((prev) => 
          prev >= (document.presentationSlides?.length || 1) - 1 ? 0 : prev + 1
        );
      }, 4000);
    } else if (slideshowTimerRef.current) {
      clearInterval(slideshowTimerRef.current);
    }
    return () => {
      if (slideshowTimerRef.current) clearInterval(slideshowTimerRef.current);
    };
  }, [isSlideshowRunning, document?.presentationSlides?.length]);

  // Keyboard navigation for presentations & modal close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      } else if (document?.fileType === 'pptx' || document?.fileType === 'ppt' || document?.presentationSlides) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          setActiveSlideIndex((prev) => 
            Math.min((document.presentationSlides?.length || 1) - 1, prev + 1)
          );
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setActiveSlideIndex((prev) => Math.max(0, prev - 1));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullscreen, document, onClose]);

  // Zoom handlers
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(250, prev + 15));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(50, prev - 15));
  const handleResetZoom = () => {
    setZoomLevel(100);
    setRotation(0);
  };
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(() => setIsFullscreen(true));
      }
      setIsFullscreen(true);
    } else {
      if (window.document.fullscreenElement) {
        window.document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };

  // Copy text handler
  const handleCopyContent = () => {
    if (!document) return;
    const textToCopy = document.rawContent || document.markdownContent || JSON.stringify(document, null, 2);
    navigator.clipboard.writeText(textToCopy);
    setCopiedText(true);
    setTimeout(() => setCopiedText(null as any), 2000);
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Download handler
  const handleDownload = () => {
    if (!document) return;
    if (document.fileUrl && !document.fileUrl.startsWith('data:')) {
      const a = window.document.createElement('a');
      a.href = document.fileUrl;
      a.download = document.metadata?.fileName || `${document.title.replace(/\s+/g, '_')}.${document.fileType}`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      return;
    }

    const content = document.rawContent || document.markdownContent || '';
    const mimeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      csv: 'text/csv',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      txt: 'text/plain',
      md: 'text/markdown'
    };

    const blob = new Blob([content], { type: mimeMap[document.fileType] || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = document.metadata?.fileName || `${document.title.replace(/\s+/g, '_')}.${document.fileType === 'certificate' ? 'pdf' : document.fileType}`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Firestore Save Action
  const handleSaveToFirestoreClick = async () => {
    if (!document) return;
    if (onSaveToFirestore) {
      try {
        await onSaveToFirestore(document);
        setFirestoreSavedSuccess(true);
      } catch (e) {
        console.error('Firestore save failed:', e);
      }
    }
  };

  // Spreadsheet Data calculation & Sorting
  const currentSheet: SpreadsheetSheet | null = useMemo(() => {
    if (document?.spreadsheetData && document.spreadsheetData.length > 0) {
      return document.spreadsheetData[activeSheetIndex] || document.spreadsheetData[0] || null;
    }
    return null;
  }, [document?.spreadsheetData, activeSheetIndex]);

  const filteredSpreadsheetRows = useMemo(() => {
    if (!currentSheet) return [];
    let rows = [...currentSheet.rows];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((row) =>
        row.some((cell) => cell !== null && String(cell).toLowerCase().includes(q))
      );
    }

    if (sortColumn) {
      rows.sort((a, b) => {
        const valA = a[sortColumn.index];
        const valB = b[sortColumn.index];
        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        const isNum = typeof valA === 'number' && typeof valB === 'number';
        if (isNum) {
          return sortColumn.direction === 'asc' ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
        }
        return sortColumn.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return rows;
  }, [currentSheet, searchQuery, sortColumn]);

  // Statistics for selected column or whole sheet
  const spreadsheetStats = useMemo(() => {
    if (!currentSheet || filteredSpreadsheetRows.length === 0) return null;
    let numericSum = 0;
    let numericCount = 0;
    let minVal = Infinity;
    let maxVal = -Infinity;

    const colIdx = selectedCell ? selectedCell.col : 0;
    filteredSpreadsheetRows.forEach((row) => {
      const val = row[colIdx];
      if (typeof val === 'number') {
        numericSum += val;
        numericCount++;
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
      } else if (typeof val === 'string') {
        const cleaned = val.replace(/[\$,]/g, '').trim();
        const parsed = parseFloat(cleaned);
        if (!isNaN(parsed)) {
          numericSum += parsed;
          numericCount++;
          if (parsed < minVal) minVal = parsed;
          if (parsed > maxVal) maxVal = parsed;
        }
      }
    });

    return {
      totalRows: currentSheet.rows.length,
      filteredRows: filteredSpreadsheetRows.length,
      totalCols: currentSheet.headers.length,
      colHeader: currentSheet.headers[colIdx] || `Col ${colIdx + 1}`,
      numericCount,
      sum: numericCount > 0 ? numericSum : null,
      avg: numericCount > 0 ? numericSum / numericCount : null,
      min: numericCount > 0 && minVal !== Infinity ? minVal : null,
      max: numericCount > 0 && maxVal !== -Infinity ? maxVal : null
    };
  }, [currentSheet, filteredSpreadsheetRows, selectedCell]);

  if (!isOpen || !document) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in"
      id="modal-document-viewer"
    >
      <div 
        ref={containerRef}
        className={`bg-slate-900 border border-slate-700/70 text-slate-100 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-200 ${
          isFullscreen ? 'w-screen h-screen rounded-none border-none' : 'w-full max-w-6xl h-[92vh]'
        }`}
      >
        {/* TOP TOOLBAR */}
        <header className="px-4 py-3 bg-slate-900/95 border-b border-slate-800 flex items-center justify-between gap-3 shrink-0 select-none">
          {/* Document Type Badge & Title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-xl shrink-0 ${
              document.fileType === 'pdf' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
              document.fileType === 'docx' || document.fileType === 'doc' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
              document.fileType === 'xlsx' || document.fileType === 'xls' || document.fileType === 'csv' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
              document.fileType === 'pptx' || document.fileType === 'ppt' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
              document.fileType === 'certificate' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' :
              'bg-slate-700/40 text-slate-300 border border-slate-600'
            }`}>
              {document.fileType === 'pdf' && <FileText className="w-5 h-5" />}
              {(document.fileType === 'docx' || document.fileType === 'doc') && <FileCheck className="w-5 h-5" />}
              {(document.fileType === 'xlsx' || document.fileType === 'xls' || document.fileType === 'csv') && <Table className="w-5 h-5" />}
              {(document.fileType === 'pptx' || document.fileType === 'ppt') && <Presentation className="w-5 h-5" />}
              {document.fileType === 'certificate' && <Award className="w-5 h-5" />}
              {document.fileType === 'txt' || document.fileType === 'md' ? <FileText className="w-5 h-5" /> : null}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm sm:text-base text-slate-100 truncate">
                  {document.title}
                </h3>
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                  {document.fileType}
                </span>
                {firestoreSavedSuccess && (
                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-950/70 border border-emerald-500/40 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                    <span>Firestore Synced</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 truncate">
                {document.metadata?.issuingAuthority ? `${document.metadata.issuingAuthority} • ` : ''}
                {document.metadata?.incorporationDate ? `Formation Date: ${document.metadata.incorporationDate} • ` : ''}
                {document.metadata?.filingNumber ? `File #${document.metadata.filingNumber}` : (document.metadata?.fileName || 'Official In-App Document Preview')}
              </p>
            </div>
          </div>

          {/* Action Buttons Toolbar */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Save to Firestore Button */}
            {onSaveToFirestore && (
              <button
                type="button"
                id="btn-viewer-save-firestore"
                onClick={handleSaveToFirestoreClick}
                disabled={isSavingToFirestore || firestoreSavedSuccess}
                className={`px-3 py-1.5 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-all cursor-pointer ${
                  firestoreSavedSuccess 
                    ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-500/40'
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-400/40 shadow-sm active:scale-98'
                }`}
                title="Upload & Synchronize to Firestore Database"
              >
                {isSavingToFirestore ? (
                  <>
                    <RotateCw className="w-3.5 h-3.5 animate-spin text-white" />
                    <span className="hidden sm:inline">Saving to Firestore...</span>
                  </>
                ) : firestoreSavedSuccess ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="hidden sm:inline">Saved in Firestore</span>
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-3.5 h-3.5 text-white" />
                    <span className="hidden sm:inline">Save to Firestore</span>
                  </>
                )}
              </button>
            )}

            {/* Zoom Controls */}
            <div className="hidden md:flex items-center bg-slate-800/80 border border-slate-700 rounded-xl px-1.5 py-1">
              <button
                type="button"
                onClick={handleZoomOut}
                className="p-1 text-slate-400 hover:text-slate-200 rounded hover:bg-slate-700/50 cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleResetZoom}
                className="px-1.5 text-[11px] font-mono text-slate-300 hover:text-white"
                title="Reset Zoom"
              >
                {zoomLevel}%
              </button>
              <button
                type="button"
                onClick={handleZoomIn}
                className="p-1 text-slate-400 hover:text-slate-200 rounded hover:bg-slate-700/50 cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleRotate}
                className="p-1 text-slate-400 hover:text-slate-200 rounded hover:bg-slate-700/50 border-l border-slate-700 ml-1 cursor-pointer"
                title="Rotate 90°"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Copy Content */}
            <button
              type="button"
              onClick={handleCopyContent}
              className="p-2 text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all cursor-pointer"
              title="Copy Content to Clipboard"
            >
              {copiedText ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>

            {/* Print */}
            <button
              type="button"
              onClick={handlePrint}
              className="p-2 text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all cursor-pointer"
              title="Print Document"
            >
              <Printer className="w-4 h-4" />
            </button>

            {/* Download */}
            <button
              type="button"
              onClick={handleDownload}
              className="p-2 text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all cursor-pointer"
              title="Download File"
            >
              <Download className="w-4 h-4" />
            </button>

            {/* Metadata Drawer Toggle */}
            <button
              type="button"
              onClick={() => setShowMetadataDrawer(!showMetadataDrawer)}
              className={`p-2 border rounded-xl transition-all cursor-pointer ${
                showMetadataDrawer 
                  ? 'bg-indigo-600 text-white border-indigo-400' 
                  : 'bg-slate-800/80 text-slate-300 hover:text-white border-slate-700'
              }`}
              title="Document Metadata & Legal Authority"
            >
              <Info className="w-4 h-4" />
            </button>

            {/* Fullscreen */}
            <button
              type="button"
              onClick={toggleFullscreen}
              className="p-2 text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded-xl transition-all cursor-pointer"
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-rose-400 hover:text-rose-200 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/50 rounded-xl transition-all cursor-pointer ml-1"
              title="Close Viewer (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* MAIN VIEWER BODY */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* CONTENT AREA */}
          <div className="flex-1 flex flex-col bg-slate-950/60 overflow-hidden">
            {/* SUB-HEADER / SEARCH OR SHEET TABS */}
            {(document.fileType === 'xlsx' || document.fileType === 'xls' || document.fileType === 'csv' || document.spreadsheetData) && (
              <div className="px-4 py-2 bg-slate-900/90 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                {/* Sheet Tabs */}
                <div className="flex items-center gap-1 overflow-x-auto py-0.5">
                  {document.spreadsheetData?.map((sheet, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setActiveSheetIndex(idx);
                        setSelectedCell(null);
                        setSortColumn(null);
                      }}
                      className={`px-3 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                        activeSheetIndex === idx
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'bg-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                      }`}
                    >
                      <Table className="w-3.5 h-3.5" />
                      <span>{sheet.name}</span>
                      <span className="text-[10px] opacity-75">({sheet.rows.length})</span>
                    </button>
                  ))}
                </div>

                {/* Spreadsheet Search Bar */}
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Filter grid data..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-lg pl-8 pr-3 py-1 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 w-44"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {sortColumn && (
                    <button
                      type="button"
                      onClick={() => setSortColumn(null)}
                      className="text-[10px] text-slate-400 hover:text-slate-200 bg-slate-800 px-2 py-1 rounded border border-slate-700"
                    >
                      Clear Sort
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* PRESENTATION CONTROLS SUB-HEADER */}
            {(document.fileType === 'pptx' || document.fileType === 'ppt' || document.presentationSlides) && (
              <div className="px-4 py-2 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveSlideIndex((prev) => Math.max(0, prev - 1))}
                    disabled={activeSlideIndex === 0}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-200 rounded-lg border border-slate-700 cursor-pointer"
                    title="Previous Slide (←)"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-mono font-medium text-slate-300">
                    Slide {activeSlideIndex + 1} of {document.presentationSlides?.length || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveSlideIndex((prev) => Math.min((document.presentationSlides?.length || 1) - 1, prev + 1))}
                    disabled={activeSlideIndex === (document.presentationSlides?.length || 1) - 1}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-200 rounded-lg border border-slate-700 cursor-pointer"
                    title="Next Slide (→ / Space)"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsSlideshowRunning(!isSlideshowRunning)}
                    className={`ml-2 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1.5 transition-all cursor-pointer ${
                      isSlideshowRunning 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                    }`}
                  >
                    {isSlideshowRunning ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    <span>{isSlideshowRunning ? 'Pause Slideshow' : 'Auto Play'}</span>
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSlideNotes(!showSlideNotes)}
                    className={`px-2.5 py-1 text-xs rounded-lg border transition-all cursor-pointer ${
                      showSlideNotes
                        ? 'bg-slate-800 text-amber-300 border-amber-500/40'
                        : 'bg-slate-900 text-slate-400 border-slate-700'
                    }`}
                  >
                    Speaker Notes: {showSlideNotes ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            )}

            {/* SCROLLABLE VIEWPORT */}
            <div className="flex-1 overflow-auto p-4 sm:p-6 flex items-start justify-center">
              {/* 1. PDF VIEW */}
              {document.fileType === 'pdf' && (
                <div 
                  className="w-full h-full flex flex-col items-center justify-center transition-transform origin-top"
                  style={{ transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)` }}
                >
                  {document.fileUrl ? (
                    <iframe
                      src={document.fileUrl}
                      title={document.title}
                      className="w-full h-full min-h-[500px] rounded-xl border border-slate-700 bg-white"
                    />
                  ) : (
                    <div className="w-full max-w-3xl bg-white text-slate-900 rounded-xl shadow-2xl p-8 sm:p-12 min-h-[600px] border border-slate-200 space-y-6">
                      <div className="border-b-2 border-slate-900 pb-4 flex justify-between items-start">
                        <div>
                          <p className="text-[10px] font-mono tracking-widest text-slate-500 uppercase">Official Document Archive</p>
                          <h2 className="text-2xl font-serif font-bold text-slate-900">{document.title}</h2>
                          <p className="text-xs text-slate-600 mt-1">{document.metadata?.issuingAuthority || 'Department of State / Secretary of State Filing'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-mono font-bold text-indigo-900 bg-indigo-50 px-2.5 py-1 rounded border border-indigo-200">
                            FILE #{document.metadata?.filingNumber || 'DOS-OFFICIAL'}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-1">Date: {document.metadata?.filingDate || document.metadata?.incorporationDate || 'On Record'}</p>
                        </div>
                      </div>

                      <div className="prose prose-slate max-w-none text-xs sm:text-sm leading-relaxed font-serif whitespace-pre-wrap">
                        {document.rawContent || document.markdownContent || 'Official certified PDF document text rendering.'}
                      </div>

                      <div className="pt-8 mt-8 border-t border-slate-200 flex justify-between items-end text-[10px] text-slate-500 font-mono">
                        <div>
                          <p>Certified Electronic Record</p>
                          <p>Verification Code: {document.id.slice(0, 16).toUpperCase()}</p>
                        </div>
                        <div className="text-right">
                          <p>Department of State / State Division of Corporations</p>
                          <p>Official Statutory Transcript</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 2. OFFICIAL CERTIFICATE OF INCORPORATION / GOOD STANDING */}
              {document.fileType === 'certificate' && (
                <div 
                  className="w-full max-w-3xl transition-transform origin-top"
                  style={{ transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)` }}
                >
                  <div className="bg-gradient-to-b from-amber-50/40 via-white to-amber-50/30 text-slate-900 rounded-xl shadow-2xl p-8 sm:p-14 border-8 border-double border-indigo-950 relative overflow-hidden">
                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none select-none">
                      <Landmark className="w-96 h-96 text-indigo-900" />
                    </div>

                    {/* Certificate Top Header */}
                    <div className="text-center space-y-2 relative z-10">
                      <div className="w-16 h-16 mx-auto rounded-full border-2 border-amber-600 bg-amber-100 flex items-center justify-center shadow-inner">
                        <Award className="w-9 h-9 text-amber-700" />
                      </div>
                      <p className="text-xs font-serif font-bold uppercase tracking-widest text-indigo-950">
                        {document.certificateData?.state || document.metadata?.state || 'State'} Department of State
                      </p>
                      <p className="text-[10px] font-serif uppercase tracking-wider text-slate-600">
                        Division of Corporations, State Records & Uniform Commercial Code
                      </p>
                      <h1 className="text-2xl sm:text-3xl font-serif font-extrabold text-indigo-950 pt-2 tracking-wide">
                        {document.title}
                      </h1>
                      <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-amber-600 to-transparent mx-auto mt-2" />
                    </div>

                    {/* Certificate Body Verbiage */}
                    <div className="mt-8 space-y-6 text-xs sm:text-sm font-serif leading-relaxed text-slate-800 relative z-10">
                      <p className="text-justify indent-6">
                        I, Secretary of State / Registrar of Corporations of the State of{' '}
                        <strong>{document.certificateData?.state || document.metadata?.state || 'the Registered State'}</strong>, 
                        do hereby certify that statutory articles and legal documentation have been duly filed in this office pursuant to the provisions of the Business Corporation and Entity Law.
                      </p>

                      <div className="my-6 p-5 bg-amber-50/60 border border-amber-200/80 rounded-xl space-y-3 font-sans text-xs">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Legal Entity Name:</span>
                            <span className="font-bold text-indigo-950 text-sm">{document.certificateData?.companyName || document.metadata?.companyName}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Official Department of State (DOS) ID:</span>
                            <span className="font-mono font-bold text-indigo-900">{document.certificateData?.filingNumber || document.metadata?.filingNumber}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Date of Incorporation / Formation:</span>
                            <span className="font-semibold text-slate-800">{document.certificateData?.incorporationDate || document.metadata?.incorporationDate}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Entity Classification:</span>
                            <span className="font-semibold text-slate-800">{document.certificateData?.entityType || 'Domestic Business Entity'}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Official Standing:</span>
                            <span className="inline-flex items-center gap-1 font-bold text-emerald-800 bg-emerald-100/80 px-2 py-0.5 rounded text-[11px]">
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                              {document.certificateData?.status || 'Active / In Good Standing'}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Statutory Duration:</span>
                            <span className="font-semibold text-slate-800">{document.certificateData?.duration || 'Perpetual'}</span>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-amber-200/60">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Registered Agent & Service of Process:</span>
                          <span className="font-medium text-slate-800">{document.certificateData?.registeredAgentName || 'Agent of Record on File'}</span>
                          <p className="text-[11px] text-slate-600">{document.certificateData?.registeredAgentAddress || document.metadata?.principalAddress}</p>
                        </div>
                      </div>

                      <p className="text-justify indent-6">
                        I FURTHER CERTIFY that the initial Certificate of Incorporation and subsequent statutory amendments have been examined and are on record; and that no certificate of dissolution has been filed.
                      </p>
                    </div>

                    {/* Certificate Footer & Seal */}
                    <div className="mt-12 pt-6 border-t border-amber-300 flex justify-between items-end relative z-10">
                      <div className="space-y-1">
                        <div className="w-20 h-20 rounded-full border-4 border-amber-600/70 bg-amber-50 flex flex-col items-center justify-center text-center p-1 shadow-sm">
                          <Landmark className="w-5 h-5 text-amber-800" />
                          <span className="text-[7px] font-serif font-bold uppercase text-amber-900">Official Seal</span>
                          <span className="text-[6px] text-amber-800">State of {document.certificateData?.state || 'Record'}</span>
                        </div>
                        <p className="text-[9px] font-mono text-slate-500">Authenticity Ref: {document.id.slice(0, 12).toUpperCase()}</p>
                      </div>

                      <div className="text-right space-y-1">
                        <div className="w-44 border-b border-slate-900 pb-1 text-center font-serif italic text-sm text-indigo-950 font-semibold">
                          {document.certificateData?.issuingOfficer || 'Office of Secretary of State'}
                        </div>
                        <p className="text-[11px] font-bold text-slate-900">{document.certificateData?.issuingOfficerTitle || 'Secretary of State / Registrar'}</p>
                        <p className="text-[9px] text-slate-500">Division of Corporations</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 3. WORD DOCUMENT (.DOCX, .DOC, .RTF, .TXT, .MD) */}
              {(document.fileType === 'docx' || document.fileType === 'doc' || document.fileType === 'txt' || document.fileType === 'md') && (
                <div 
                  className="w-full max-w-4xl transition-transform origin-top"
                  style={{ transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)` }}
                >
                  <div className="bg-white text-slate-900 rounded-xl shadow-2xl p-8 sm:p-14 min-h-[700px] border border-slate-200 space-y-6">
                    {/* Document Header */}
                    <div className="border-b border-slate-200 pb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Document Manuscript</p>
                          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{document.title}</h2>
                          <p className="text-xs text-slate-500 mt-1">{document.metadata?.description || document.metadata?.companyName}</p>
                        </div>
                        <div className="text-right text-xs text-slate-400">
                          <p className="font-mono">{document.metadata?.fileName || 'document.docx'}</p>
                          <p className="text-[10px] mt-0.5">Format: {document.fileType.toUpperCase()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Document Body */}
                    <div className="prose prose-slate max-w-none text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-sans">
                      {document.markdownContent || document.rawContent || (
                        <div className="text-slate-400 italic py-12 text-center">
                          Empty document manuscript or binary format preview.
                        </div>
                      )}
                    </div>

                    {/* Document Footer */}
                    <div className="pt-6 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 font-mono">
                      <span>In-App Word Document Viewer</span>
                      <span>Page 1 of 1</span>
                    </div>
                  </div>
                </div>
              )}

              {/* 4. SPREADSHEET (.XLSX, .XLS, .CSV) DATA GRID */}
              {(document.fileType === 'xlsx' || document.fileType === 'xls' || document.fileType === 'csv' || document.spreadsheetData) && (
                <div className="w-full h-full flex flex-col space-y-4">
                  {currentSheet ? (
                    <div className="w-full bg-slate-900 border border-slate-700/80 rounded-xl shadow-xl overflow-hidden flex flex-col">
                      {/* Active Cell Inspector */}
                      <div className="px-4 py-2 bg-slate-800/70 border-b border-slate-700 flex items-center justify-between text-xs text-slate-300">
                        <div className="flex items-center gap-3">
                          <span className="font-mono bg-slate-950 px-2 py-0.5 rounded border border-slate-700 text-emerald-400 font-bold">
                            {selectedCell ? `${String.fromCharCode(65 + selectedCell.col)}${selectedCell.row + 1}` : 'A1'}
                          </span>
                          <span className="font-mono text-slate-200 truncate max-w-md">
                            {selectedCell ? String(selectedCell.val) : (currentSheet.rows[0]?.[0] !== undefined ? String(currentSheet.rows[0][0]) : '')}
                          </span>
                        </div>

                        {spreadsheetStats && (
                          <div className="hidden lg:flex items-center gap-4 text-[11px] text-slate-400">
                            <span>Rows: <strong className="text-slate-200">{spreadsheetStats.filteredRows}</strong></span>
                            <span>Cols: <strong className="text-slate-200">{spreadsheetStats.totalCols}</strong></span>
                            {spreadsheetStats.sum !== null && (
                              <span>Sum: <strong className="text-emerald-400 font-mono">${spreadsheetStats.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></span>
                            )}
                            {spreadsheetStats.avg !== null && (
                              <span>Avg: <strong className="text-indigo-300 font-mono">${spreadsheetStats.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Interactive Data Table Grid */}
                      <div className="overflow-auto max-h-[62vh]">
                        <table className="w-full text-left text-xs border-collapse font-sans">
                          <thead className="sticky top-0 bg-slate-800 text-slate-200 z-10 select-none shadow-sm">
                            <tr className="border-b border-slate-700">
                              <th className="w-12 py-2 px-3 text-center bg-slate-850 font-mono text-[10px] text-slate-400 border-r border-slate-700">
                                #
                              </th>
                              {currentSheet.headers.map((header, colIdx) => (
                                <th
                                  key={colIdx}
                                  onClick={() => {
                                    setSortColumn((prev) => {
                                      if (prev?.index === colIdx) {
                                        return prev.direction === 'asc' ? { index: colIdx, direction: 'desc' } : null;
                                      }
                                      return { index: colIdx, direction: 'asc' };
                                    });
                                  }}
                                  className="py-2.5 px-3 font-semibold text-slate-200 border-r border-slate-700 hover:bg-slate-700/60 transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-[10px] text-slate-500 uppercase">
                                        {String.fromCharCode(65 + colIdx)}
                                      </span>
                                      <span>{header}</span>
                                    </div>
                                    <ArrowUpDown className={`w-3 h-3 ${sortColumn?.index === colIdx ? 'text-emerald-400' : 'text-slate-500'}`} />
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800">
                            {filteredSpreadsheetRows.map((row, rowIdx) => (
                              <tr 
                                key={rowIdx} 
                                className="hover:bg-slate-800/40 transition-colors"
                              >
                                <td className="py-2 px-3 text-center font-mono text-[10px] text-slate-500 bg-slate-900/60 border-r border-slate-800 select-none">
                                  {rowIdx + 1}
                                </td>
                                {row.map((cell, colIdx) => {
                                  const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx;
                                  return (
                                    <td
                                      key={colIdx}
                                      onClick={() => setSelectedCell({ row: rowIdx, col: colIdx, val: cell })}
                                      className={`py-2 px-3 border-r border-slate-800 font-mono text-[11px] whitespace-nowrap cursor-cell transition-all ${
                                        isSelected 
                                          ? 'bg-emerald-950/80 ring-2 ring-emerald-500 text-emerald-200 font-bold' 
                                          : 'text-slate-300'
                                      }`}
                                    >
                                      {cell === null || cell === undefined ? '' : String(cell)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="p-12 text-center text-slate-400">No spreadsheet sheet data found.</div>
                  )}
                </div>
              )}

              {/* 5. PRESENTATION (.PPTX, .PPT) SLIDE DECK */}
              {(document.fileType === 'pptx' || document.fileType === 'ppt' || document.presentationSlides) && (
                <div className="w-full max-w-4xl flex flex-col items-center gap-4">
                  {document.presentationSlides && document.presentationSlides.length > 0 ? (
                    (() => {
                      const slide = document.presentationSlides[activeSlideIndex] || document.presentationSlides[0];
                      return (
                        <div className="w-full space-y-4">
                          {/* 16:9 Slide Canvas */}
                          <div className="w-full aspect-[16/9] bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl shadow-2xl p-8 sm:p-12 border border-indigo-800/50 flex flex-col justify-between relative overflow-hidden select-none">
                            {/* Slide Decorative Accent */}
                            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

                            {/* Slide Header */}
                            <div className="relative z-10">
                              <div className="flex justify-between items-center text-xs text-indigo-300 pb-4 border-b border-indigo-900/60">
                                <span className="font-semibold tracking-wider uppercase text-[10px] text-indigo-400">{document.title}</span>
                                <span className="font-mono text-[10px] bg-white/10 px-2 py-0.5 rounded">
                                  {activeSlideIndex + 1} / {document.presentationSlides.length}
                                </span>
                              </div>
                              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-4 tracking-tight">
                                {slide.title}
                              </h2>
                              {slide.subtitle && (
                                <p className="text-sm text-indigo-200 mt-1">{slide.subtitle}</p>
                              )}
                            </div>

                            {/* Slide Main Content */}
                            <div className="relative z-10 my-4 space-y-3">
                              {slide.highlightStat && (
                                <div className="p-4 bg-white/5 border border-indigo-400/30 rounded-xl inline-block">
                                  <div className="text-3xl font-bold text-emerald-400 font-mono">{slide.highlightStat.value}</div>
                                  <div className="text-xs text-slate-300 mt-0.5">{slide.highlightStat.label}</div>
                                </div>
                              )}

                              {slide.bullets && slide.bullets.length > 0 && (
                                <ul className="space-y-2.5 text-xs sm:text-sm text-slate-200">
                                  {slide.bullets.map((bullet, bIdx) => (
                                    <li key={bIdx} className="flex items-start gap-2.5">
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                                      <span className="leading-relaxed">{bullet}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {slide.content && (
                                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">{slide.content}</p>
                              )}

                              {slide.callout && (
                                <div className="p-3 bg-indigo-900/40 border-l-4 border-indigo-400 rounded text-xs text-indigo-200">
                                  {slide.callout}
                                </div>
                              )}
                            </div>

                            {/* Slide Footer */}
                            <div className="relative z-10 pt-4 border-t border-indigo-900/60 flex justify-between items-center text-[10px] text-slate-400">
                              <span>Official Corporate Briefing Deck</span>
                              <span>Confidential & Proprietary</span>
                            </div>
                          </div>

                          {/* Speaker Notes Drawer */}
                          {showSlideNotes && slide.notes && (
                            <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl text-xs text-slate-300">
                              <p className="font-bold text-[10px] uppercase text-amber-400 tracking-wider mb-1">Speaker Notes:</p>
                              <p className="leading-relaxed text-slate-400">{slide.notes}</p>
                            </div>
                          )}

                          {/* Thumbnails Navigator Carousel */}
                          <div className="flex items-center gap-2 overflow-x-auto py-2">
                            {document.presentationSlides.map((s, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setActiveSlideIndex(idx)}
                                className={`w-28 h-16 rounded-lg p-2 text-left shrink-0 transition-all border flex flex-col justify-between cursor-pointer ${
                                  activeSlideIndex === idx
                                    ? 'bg-indigo-900/80 border-indigo-400 shadow-md ring-2 ring-indigo-500'
                                    : 'bg-slate-900 border-slate-800 hover:border-slate-700 opacity-60 hover:opacity-100'
                                }`}
                              >
                                <span className="text-[9px] font-bold text-white truncate block">{s.title}</span>
                                <span className="text-[8px] font-mono text-indigo-300">Slide {idx + 1}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="p-12 text-center text-slate-400">No slide content available.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT METADATA & LEGAL AUTHORITY DRAWER */}
          {showMetadataDrawer && (
            <div className="w-80 bg-slate-900 border-l border-slate-800 p-5 overflow-y-auto space-y-5 text-xs text-slate-300 shrink-0 select-none animate-slide-in-right">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <h4 className="font-bold text-sm text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-400" />
                  Document Attributes
                </h4>
                <button
                  type="button"
                  onClick={() => setShowMetadataDrawer(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Firestore Status Card */}
              <div className="p-3.5 bg-slate-850 border border-slate-750 rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Firestore Persistence</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    firestoreSavedSuccess 
                      ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/40' 
                      : 'bg-amber-950 text-amber-300 border border-amber-500/40'
                  }`}>
                    {firestoreSavedSuccess ? 'Active in Firestore' : 'Uncommitted'}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  {firestoreSavedSuccess
                    ? 'Document metadata and certificate content is permanently stored in your Firestore database.'
                    : 'Click "Save to Firestore" to persist this document to your cloud database.'}
                </p>
                {onSaveToFirestore && !firestoreSavedSuccess && (
                  <button
                    type="button"
                    onClick={handleSaveToFirestoreClick}
                    disabled={isSavingToFirestore}
                    className="w-full py-1.5 px-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold text-xs transition-all flex items-center justify-center gap-1.5"
                  >
                    <CloudUpload className="w-3.5 h-3.5" />
                    <span>Upload to Firestore Database</span>
                  </button>
                )}
              </div>

              {/* Entity & Filing Details */}
              <div className="space-y-3">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Issuing Authority</span>
                  <span className="font-semibold text-slate-100">{document.metadata?.issuingAuthority || 'Department of State'}</span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Official File Number / DOS ID</span>
                  <span className="font-mono text-indigo-300 font-semibold">{document.metadata?.filingNumber || 'DOS-RECORD'}</span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Date of Incorporation / Filing</span>
                  <span className="text-slate-200 font-medium">{document.metadata?.incorporationDate || document.metadata?.filingDate || 'Recorded'}</span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Legal Entity Name</span>
                  <span className="text-slate-200 font-semibold">{document.metadata?.companyName || 'Corporate Entity'}</span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Document Type</span>
                  <span className="text-slate-200 capitalize">{document.metadata?.documentType || document.fileType}</span>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">File Format</span>
                  <span className="font-mono text-slate-300 uppercase">{document.fileType}</span>
                </div>
              </div>

              {/* Legal Disclaimer */}
              <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl text-[10px] text-slate-500 leading-relaxed">
                Official document records rendered with verified Department of State and statutory filing metadata.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
